import { FC, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import {
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Edit,
  Cancel,
  Add,
  Delete,
  Archive,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledDateField from "../../components/controlledComponents/ControlledDateField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { logger } from "../../../utils/common";
import {
  useInstituteByIdHook,
  useUpdateInstituteHook,
  useAddressesHook,
  useCreateAddressHook,
  useUpdateAddressHook,
  useDeleteAddressHook,
  useArchiveInstituteHook,
} from "../../../hooks/useInstituteHook";
import {
  TInstitute,
  TInstituteType,
  TAddress,
  TAddressType,
} from "../../../apis/institute/institute.interfaces";
import {
  formatInstituteStatusLabel,
  instituteStatusChipColor,
} from "./institute.helpers";

const isInstituteContentLocked = (i: TInstitute) =>
  !!i.deletedAt || i.status === "archived";

const INSTITUTE_DETAIL_EDIT_STATUSES = [
  "draft",
  "pending",
  "active",
  "other",
] as const;

const instituteStatusForEditForm = (
  status: TInstitute["status"],
): (typeof INSTITUTE_DETAIL_EDIT_STATUSES)[number] =>
  (INSTITUTE_DETAIL_EDIT_STATUSES as readonly string[]).includes(status)
    ? (status as (typeof INSTITUTE_DETAIL_EDIT_STATUSES)[number])
    : "draft";

const instituteTypes: TInstituteType[] = [
  "CBSE",
  "ICSE",
  "State Board",
  "IB",
  "IGCSE",
  "Other",
];

// Indian phone number validation: supports +91XXXXXXXXXX, 0XXXXXXXXXX, or XXXXXXXXXX (10 digits starting with 6-9)
const indianPhoneRegex = /^(\+91[6-9]\d{9}|0[6-9]\d{9}|[6-9]\d{9})$/;
const urlRegex =
  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
// Indian postal code validation: 6 digits
const indianPostalCodeRegex = /^\d{6}$/;

const addressTypes: TAddressType[] = ["correspondence", "registered", "other"];

const addressSchema = z.object({
  type: z.enum(["correspondence", "registered", "other"], {
    errorMap: () => ({ message: "Please select a valid address type" }),
  }),
  streetAddress: z
    .string()
    .min(1, "Street address is required")
    .min(5, "Street address must be at least 5 characters")
    .max(200, "Street address must not exceed 200 characters"),
  city: z
    .string()
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(100, "City must not exceed 100 characters")
    .regex(/^[A-Za-z\s]+$/, "City should only contain letters and spaces"),
  state: z
    .string()
    .min(1, "State is required")
    .min(2, "State must be at least 2 characters")
    .max(100, "State must not exceed 100 characters")
    .regex(/^[A-Za-z\s]+$/, "State should only contain letters and spaces"),
  postalCode: z
    .string()
    .min(1, "Postal code is required")
    .regex(indianPostalCodeRegex, "Postal code must be exactly 6 digits"),
  country: z
    .string()
    .min(1, "Country is required")
    .min(2, "Country must be at least 2 characters")
    .max(100, "Country must not exceed 100 characters")
    .regex(/^[A-Za-z\s]+$/, "Country should only contain letters and spaces"),
  isPrimary: z.boolean().default(false),
});

type AddressFormData = z.infer<typeof addressSchema>;

const updateInstituteSchema = z.object({
  name: z
    .string()
    .min(1, "Institute name is required")
    .min(2, "Institute name must be at least 2 characters")
    .max(100, "Institute name must not exceed 100 characters"),
  type: z.enum(["CBSE", "ICSE", "State Board", "IB", "IGCSE", "Other"], {
    errorMap: () => ({ message: "Please select a valid institute type" }),
  }),
  registrationNumber: z
    .string()
    .min(1, "Registration number is required")
    .min(3, "Registration number must be at least 3 characters")
    .max(50, "Registration number must not exceed 50 characters"),
  registrationDate: z
    .any()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const date = dayjs.isDayjs(val) ? val : dayjs(val);
      return date.isValid();
    }, "Please enter a valid date"),
  legalEntity: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 2,
      "Legal entity must be at least 2 characters",
    )
    .refine(
      (val) => !val || val.length <= 200,
      "Legal entity must not exceed 200 characters",
    ),
  status: z.enum(["draft", "pending", "active", "other"], {
    errorMap: () => ({ message: "Please select a valid status" }),
  }),
  contactInformation: z
    .object({
      primaryPhone: z
        .string()
        .optional()
        .refine(
          (val) => !val || indianPhoneRegex.test(val.replace(/\s+/g, "")),
          "Please enter a valid Indian phone number (e.g., +91XXXXXXXXXX, 0XXXXXXXXXX, or XXXXXXXXXX)",
        ),
      secondaryPhone: z
        .string()
        .optional()
        .refine(
          (val) => !val || indianPhoneRegex.test(val.replace(/\s+/g, "")),
          "Please enter a valid Indian phone number (e.g., +91XXXXXXXXXX, 0XXXXXXXXXX, or XXXXXXXXXX)",
        ),
      email: z
        .string()
        .optional()
        .refine(
          (val) => !val || z.string().email().safeParse(val).success,
          "Please enter a valid email address",
        ),
      website: z
        .string()
        .optional()
        .refine(
          (val) => !val || urlRegex.test(val),
          "Please enter a valid website URL",
        ),
      fax: z
        .string()
        .optional()
        .refine(
          (val) => !val || indianPhoneRegex.test(val.replace(/\s+/g, "")),
          "Please enter a valid Indian phone number (e.g., +91XXXXXXXXXX, 0XXXXXXXXXX, or XXXXXXXXXX)",
        ),
    })
    .optional(),
});

type UpdateInstituteFormData = z.infer<typeof updateInstituteSchema>;

const InstituteDetailView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<TAddress | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const { data: institute, isLoading, error } = useInstituteByIdHook(id);
  const updateInstituteMutation = useUpdateInstituteHook();
  const { data: addresses = [], isLoading: addressesLoading } =
    useAddressesHook(id);
  const createAddressMutation = useCreateAddressHook();
  const updateAddressMutation = useUpdateAddressHook();
  const deleteAddressMutation = useDeleteAddressHook();
  const archiveInstituteMutation = useArchiveInstituteHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateInstituteFormData>({
    resolver: zodResolver(updateInstituteSchema),
    defaultValues: {
      name: "",
      type: "CBSE",
      registrationNumber: "",
      registrationDate: null,
      legalEntity: "",
      status: "draft",
      contactInformation: {
        primaryPhone: "",
        secondaryPhone: "",
        email: "",
        website: "",
        fax: "",
      },
    },
  });

  const {
    control: addressControl,
    handleSubmit: handleAddressSubmit,
    reset: resetAddressForm,
    formState: { errors: addressErrors, isSubmitting: isAddressSubmitting },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      type: "correspondence",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      country: "India",
      isPrimary: false,
    },
  });

  // Reset form when institute data loads
  useEffect(() => {
    if (institute && !isEditMode) {
      reset({
        name: institute.name,
        type: institute.type,
        registrationNumber: institute.registrationNumber,
        registrationDate: institute.registrationDate
          ? dayjs(institute.registrationDate)
          : null,
        legalEntity: institute.legalEntity || "",
        status: instituteStatusForEditForm(institute.status),
        contactInformation: {
          primaryPhone: institute.contactInformation?.primaryPhone || "",
          secondaryPhone: institute.contactInformation?.secondaryPhone || "",
          email: institute.contactInformation?.email || "",
          website: institute.contactInformation?.website || "",
          fax: institute.contactInformation?.fax || "",
        },
      });
    }
  }, [institute, isEditMode, reset]);

  const handleEdit = () => {
    if (institute && !isInstituteContentLocked(institute)) {
      reset({
        name: institute.name,
        type: institute.type,
        registrationNumber: institute.registrationNumber,
        registrationDate: institute.registrationDate
          ? dayjs(institute.registrationDate)
          : null,
        legalEntity: institute.legalEntity || "",
        status: instituteStatusForEditForm(institute.status),
        contactInformation: {
          primaryPhone: institute.contactInformation?.primaryPhone || "",
          secondaryPhone: institute.contactInformation?.secondaryPhone || "",
          email: institute.contactInformation?.email || "",
          website: institute.contactInformation?.website || "",
          fax: institute.contactInformation?.fax || "",
        },
      });
      setIsEditMode(true);
    }
  };

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!id) return;

    try {
      await archiveInstituteMutation.mutateAsync(id);
      logger.show_success("Institute archived successfully!");
      setArchiveDialogOpen(false);
      navigate(pages.institute.path);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to archive institute. Please try again.",
      );
    }
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
  };

  const handleCancel = () => {
    if (institute) {
      reset({
        name: institute.name,
        type: institute.type,
        registrationNumber: institute.registrationNumber,
        registrationDate: institute.registrationDate
          ? dayjs(institute.registrationDate)
          : null,
        legalEntity: institute.legalEntity || "",
        status: instituteStatusForEditForm(institute.status),
        contactInformation: {
          primaryPhone: institute.contactInformation?.primaryPhone || "",
          secondaryPhone: institute.contactInformation?.secondaryPhone || "",
          email: institute.contactInformation?.email || "",
          website: institute.contactInformation?.website || "",
          fax: institute.contactInformation?.fax || "",
        },
      });
    }
    setIsEditMode(false);
  };

  const onSubmit = async (data: UpdateInstituteFormData) => {
    if (!id) return;

    try {
      const registrationDate = data.registrationDate
        ? dayjs.isDayjs(data.registrationDate)
          ? data.registrationDate
          : dayjs(data.registrationDate)
        : undefined;

      const contactInfo = data.contactInformation;
      const contactInformation =
        contactInfo &&
          (contactInfo.primaryPhone ||
            contactInfo.secondaryPhone ||
            contactInfo.email ||
            contactInfo.website ||
            contactInfo.fax)
          ? {
            primaryPhone: contactInfo.primaryPhone || undefined,
            secondaryPhone: contactInfo.secondaryPhone || undefined,
            email: contactInfo.email || undefined,
            website: contactInfo.website || undefined,
            fax: contactInfo.fax || undefined,
          }
          : undefined;

      await updateInstituteMutation.mutateAsync({
        id,
        request: {
          name: data.name,
          type: data.type,
          registrationNumber: data.registrationNumber,
          registrationDate: registrationDate?.toISOString(),
          legalEntity: data.legalEntity,
          status: data.status,
          contactInformation,
        },
      });

      logger.show_success("Institute updated successfully!");
      setIsEditMode(false);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to update institute. Please try again.",
      );
    }
  };

  const instituteTypeOptions = instituteTypes.map((type) => ({
    key: type,
    value: type,
  }));

  const instituteStatusEditOptions = INSTITUTE_DETAIL_EDIT_STATUSES.map(
    (s) => ({
      key: s,
      value: formatInstituteStatusLabel(s),
    }),
  );

  const addressTypeOptions = addressTypes.map((type) => ({
    key: type,
    value: type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const handleAddAddress = () => {
    setEditingAddress(null);
    resetAddressForm({
      type: "correspondence",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      country: "India",
      isPrimary: addresses.length === 0,
    });
    setAddressDialogOpen(true);
  };

  const handleEditAddress = (address: TAddress) => {
    setEditingAddress(address);
    resetAddressForm({
      type: address.type,
      streetAddress: address.streetAddress,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      isPrimary: address.isPrimary,
    });
    setAddressDialogOpen(true);
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this address?"))
      return;

    try {
      await deleteAddressMutation.mutateAsync({ instituteId: id, addressId });
      logger.show_success("Address deleted successfully!");
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete address. Please try again.",
      );
    }
  };

  const handleAddressDialogClose = () => {
    setAddressDialogOpen(false);
    setEditingAddress(null);
    resetAddressForm({
      type: "correspondence",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      country: "India",
      isPrimary: false,
    });
  };

  const onAddressSubmit = async (data: AddressFormData) => {
    if (!id) return;

    try {
      if (editingAddress) {
        await updateAddressMutation.mutateAsync({
          instituteId: id,
          addressId: editingAddress.id,
          request: {
            type: data.type,
            streetAddress: data.streetAddress,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            country: data.country,
            isPrimary: data.isPrimary,
          },
        });
        logger.show_success("Address updated successfully!");
      } else {
        await createAddressMutation.mutateAsync({
          instituteId: id,
          type: data.type,
          streetAddress: data.streetAddress,
          city: data.city,
          state: data.state,
          postalCode: data.postalCode,
          country: data.country,
          isPrimary: data.isPrimary,
        });
        logger.show_success("Address added successfully!");
      }
      handleAddressDialogClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to save address. Please try again.",
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS.title.institute} - Detail`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="institute-detail-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !institute) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS.title.institute} - Detail`} />
        <Box p={3} data-testid="institute-detail-view">
          <MainTitle title="Institute Detail" onBackButtonClickHandler={() => navigate(pages.institute.path)} />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.message || "Institute not found. Please try again later."}
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.institute} - ${institute.name}`}
      />

      <Grid container spacing={3} data-testid="institute-detail-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="institute-detail-header"
          >
            <MainTitle title="Institute Detail" onBackButtonClickHandler={() => navigate(pages.institute.path)} />
            <Box display="flex" gap={2}>
              {!isEditMode && !isInstituteContentLocked(institute) && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleEdit}
                    data-testid="edit-institute-button"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Archive />}
                    onClick={handleArchiveClick}
                    data-testid="archive-institute-button"
                  >
                    Archive
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="institute-detail-card">
            <CardContent>
              {updateInstituteMutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {updateInstituteMutation.error?.message ||
                    "Failed to update institute. Please try again."}
                </Alert>
              )}

              {isEditMode ? (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  data-testid="institute-edit-form"
                >
                  <Grid container spacing={3}>
                    <ControlledTextField
                      control={control as any}
                      name="name"
                      id="institute-name"
                      label="Institute Name"
                      required
                      errorMessage={errors.name?.message}
                    />

                    <ControlledSelectField
                      control={control as any}
                      name="type"
                      id="institute-type"
                      label="Institute Type"
                      itemList={instituteTypeOptions}
                      isRequired
                      errorMessage={errors.type?.message}
                    />

                    <ControlledSelectField
                      control={control as any}
                      name="status"
                      id="institute-status"
                      label="Status"
                      itemList={instituteStatusEditOptions}
                      isRequired
                      errorMessage={errors.status?.message}
                    />

                    <ControlledTextField
                      control={control as any}
                      name="registrationNumber"
                      id="registration-number"
                      label="Registration Number"
                      required
                      errorMessage={errors.registrationNumber?.message}
                    />

                    <ControlledDateField
                      control={control as any}
                      name="registrationDate"
                      id="registration-date"
                      label="Registration Date"
                      errorMessage={errors.registrationDate?.message as string}
                    />

                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control as any}
                        isGridItem={false}
                        name="legalEntity"
                        id="legal-entity"
                        label="Legal Entity Information"
                        multiline
                        fullWidth
                        rows={3}
                        errorMessage={errors.legalEntity?.message}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Contact Information
                      </Typography>
                    </Grid>

                    <ControlledTextField
                      control={control as any}
                      name="contactInformation.primaryPhone"
                      id="primary-phone"
                      label="Primary Phone"
                      errorMessage={
                        errors.contactInformation?.primaryPhone?.message
                      }
                      placeholder="+91XXXXXXXXXX or 0XXXXXXXXXX"
                    />

                    <ControlledTextField
                      control={control as any}
                      name="contactInformation.secondaryPhone"
                      id="secondary-phone"
                      label="Secondary Phone"
                      errorMessage={
                        errors.contactInformation?.secondaryPhone?.message
                      }
                      placeholder="+91XXXXXXXXXX or 0XXXXXXXXXX"
                    />

                    <ControlledTextField
                      control={control as any}
                      name="contactInformation.email"
                      id="email"
                      label="Email"
                      type="email"
                      errorMessage={errors.contactInformation?.email?.message}
                    />

                    <ControlledTextField
                      control={control as any}
                      name="contactInformation.website"
                      id="website"
                      label="Website"
                      errorMessage={errors.contactInformation?.website?.message}
                      placeholder="https://example.com"
                    />

                    <ControlledTextField
                      control={control as any}
                      name="contactInformation.fax"
                      id="fax"
                      label="Fax"
                      errorMessage={errors.contactInformation?.fax?.message}
                      placeholder="+91XXXXXXXXXX or 0XXXXXXXXXX"
                    />

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          startIcon={<Cancel />}
                          onClick={handleCancel}
                          disabled={
                            isSubmitting || updateInstituteMutation.isPending
                          }
                          data-testid="cancel-edit-institute-button"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={
                            isSubmitting ||
                              updateInstituteMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Save />
                            )
                          }
                          disabled={
                            isSubmitting || updateInstituteMutation.isPending
                          }
                          data-testid="save-institute-button"
                        >
                          {isSubmitting || updateInstituteMutation.isPending
                            ? "Saving..."
                            : "Save Changes"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Institute Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {institute.name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Institute Type
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {institute.type}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Registration Number
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {institute.registrationNumber}
                    </Typography>
                  </Grid>

                  {institute.registrationDate && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Registration Date
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {dayjs(institute.registrationDate).format("MM/DD/YYYY")}
                      </Typography>
                    </Grid>
                  )}

                  {institute.legalEntity && (
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Legal Entity Information2
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {institute.legalEntity}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Status
                    </Typography>
                    <Chip
                      label={formatInstituteStatusLabel(institute.status)}
                      color={instituteStatusChipColor(institute.status)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Configuration Completeness
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {institute.configurationCompleteness}%
                    </Typography>
                  </Grid>

                  {(institute.contactInformation?.primaryPhone ||
                    institute.contactInformation?.secondaryPhone ||
                    institute.contactInformation?.email ||
                    institute.contactInformation?.website ||
                    institute.contactInformation?.fax) && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Contact Information
                          </Typography>
                        </Grid>

                        {institute.contactInformation?.primaryPhone && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Primary Phone
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {institute.contactInformation.primaryPhone}
                            </Typography>
                          </Grid>
                        )}

                        {institute.contactInformation?.secondaryPhone && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Secondary Phone
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {institute.contactInformation.secondaryPhone}
                            </Typography>
                          </Grid>
                        )}

                        {institute.contactInformation?.email && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Email
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body1" gutterBottom sx={{ mb: 0 }}>
                                {institute.contactInformation.email}
                              </Typography>
                              {institute.contactInformation.emailVerified === true && (
                                <Chip label="Verified" size="small" color="success" />
                              )}
                            </Box>
                          </Grid>
                        )}

                        {institute.contactInformation?.website && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Website
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <a
                                href={
                                  institute.contactInformation.website.startsWith(
                                    "http",
                                  )
                                    ? institute.contactInformation.website
                                    : `https://${institute.contactInformation.website}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "inherit",
                                  textDecoration: "underline",
                                }}
                              >
                                {institute.contactInformation.website}
                              </a>
                            </Typography>
                          </Grid>
                        )}

                        {institute.contactInformation?.fax && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Fax
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {institute.contactInformation.fax}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                      data-testid="addresses-section-header"
                    >
                      <Typography variant="h6">Addresses</Typography>
                      {!isInstituteContentLocked(institute) && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add />}
                          onClick={handleAddAddress}
                          data-testid="add-address-button"
                        >
                          Add Address
                        </Button>
                      )}
                    </Box>
                    {addressesLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : addresses.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        No addresses added yet. Click "Add Address" to add one.
                      </Typography>
                    ) : (
                      <Grid
                        container
                        spacing={2}
                        data-testid="addresses-list-container"
                      >
                        {addresses.map((address) => (
                          <Grid item xs={12} key={address.id}>
                            <Card
                              variant="outlined"
                              data-testid={`address-card-${address.id}`}
                            >
                              <CardContent
                                data-testid={`address-card-content-${address.id}`}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  data-testid={`address-card-inner-${address.id}`}
                                >
                                  <Box
                                    flex={1}
                                    data-testid={`address-card-info-${address.id}`}
                                  >
                                    <Box
                                      display="flex"
                                      gap={1}
                                      alignItems="center"
                                      sx={{ mb: 1 }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="medium"
                                      >
                                        {address.type.charAt(0).toUpperCase() +
                                          address.type.slice(1)}{" "}
                                        Address
                                      </Typography>
                                      {address.isPrimary && (
                                        <Chip
                                          label="Primary"
                                          size="small"
                                          color="primary"
                                        />
                                      )}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mb: 0.5 }}
                                    >
                                      {address.streetAddress}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mb: 0.5 }}
                                    >
                                      {address.city}, {address.state} -{" "}
                                      {address.postalCode}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {address.country}
                                    </Typography>
                                  </Box>
                                  {!isInstituteContentLocked(institute) && (
                                    <Box display="flex" gap={1}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleEditAddress(address)
                                        }
                                        color="primary"
                                        data-testid={`edit-address-${address.id}`}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteAddress(address.id)
                                        }
                                        color="error"
                                        data-testid={`delete-address-${address.id}`}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={addressDialogOpen}
        onClose={handleAddressDialogClose}
        maxWidth="md"
        fullWidth
        data-testid="address-dialog"
      >
        <DialogTitle data-testid="address-dialog-title">
          {editingAddress ? "Edit Address" : "Add Address"}
        </DialogTitle>
        <form
          onSubmit={handleAddressSubmit(onAddressSubmit)}
          data-testid="address-form"
        >
          <DialogContent data-testid="address-form-content">
            <Grid
              container
              spacing={2}
              sx={{ mt: 1 }}
              data-testid="address-form-container"
            >
              <ControlledSelectField
                control={addressControl as any}
                name="type"
                id="address-type"
                label="Address Type"
                itemList={addressTypeOptions}
                isRequired
                errorMessage={addressErrors.type?.message}
              />

              <ControlledTextField
                control={addressControl as any}
                name="streetAddress"
                id="street-address"
                label="Street Address"
                required
                multiline
                rows={2}
                errorMessage={addressErrors.streetAddress?.message}
              />

              <ControlledTextField
                control={addressControl as any}
                name="city"
                id="city"
                label="City"
                required
                errorMessage={addressErrors.city?.message}
              />

              <ControlledTextField
                control={addressControl as any}
                name="state"
                id="state"
                label="State"
                required
                errorMessage={addressErrors.state?.message}
              />

              <ControlledTextField
                control={addressControl as any}
                name="postalCode"
                id="postal-code"
                label="Postal Code"
                required
                errorMessage={addressErrors.postalCode?.message}
                placeholder="6 digits (e.g., 110001)"
              />

              <ControlledTextField
                control={addressControl as any}
                name="country"
                id="country"
                label="Country"
                required
                errorMessage={addressErrors.country?.message}
              />

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isPrimary"
                      control={addressControl}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Set as primary address"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions data-testid="address-dialog-actions">
            <Button
              onClick={handleAddressDialogClose}
              disabled={isAddressSubmitting}
              data-testid="cancel-address-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isAddressSubmitting ||
                createAddressMutation.isPending ||
                updateAddressMutation.isPending
              }
              startIcon={
                isAddressSubmitting ||
                  createAddressMutation.isPending ||
                  updateAddressMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <Save />
                )
              }
              data-testid="save-address-button"
            >
              {isAddressSubmitting ||
                createAddressMutation.isPending ||
                updateAddressMutation.isPending
                ? "Saving..."
                : editingAddress
                  ? "Update"
                  : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        maxWidth="sm"
        fullWidth
        data-testid="archive-institute-dialog"
      >
        <DialogTitle data-testid="archive-institute-dialog-title">
          Archive Institute
        </DialogTitle>
        <DialogContent data-testid="archive-institute-dialog-content">
          <Typography variant="body1" paragraph>
            Are you sure you want to archive this institute?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Archiving will mark this institute as archived. Archived institutes
            cannot be edited and will be filtered out from the default institute
            list view. You can still view archived institutes by using the
            filter option.
          </Typography>
          {archiveInstituteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {archiveInstituteMutation.error?.message ||
                "Failed to archive institute. Please try again."}
            </Alert>
          )}
        </DialogContent>
        <DialogActions data-testid="archive-institute-dialog-actions">
          <Button
            onClick={handleArchiveCancel}
            disabled={archiveInstituteMutation.isPending}
            data-testid="cancel-archive-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            variant="contained"
            color="error"
            disabled={archiveInstituteMutation.isPending}
            startIcon={
              archiveInstituteMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <Archive />
              )
            }
            data-testid="confirm-archive-institute-button"
          >
            {archiveInstituteMutation.isPending
              ? "Archiving..."
              : "Archive Institute"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstituteDetailView;
